import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router";

import OpenInApp from "./OpenInApp";
import Home from "./pages";
import { PrivacyPolicy } from "./pages/Legals";
import DownloadRedirect from "./pages/DownloadRedirect";

import "./globals.scss";
import TripInfos from "./pages/TripInfos";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { JoinPage } from "./pages/Join";
import { NavWrapper, NeedAuthentication } from "./components";
import Account from "./pages/Account";
import RegisterOrganisation from "./pages/Authenticate/RegisterOrganisation";
import LeavePrint from "./pages/LeavePrint";
import CalshareWiki from "./pages/Wiki";
import { EventInfos, EventsCentre } from "./pages/Events";
import GoWithCalshare from "./pages/GoWithCalshare";
import Organisations from "./pages/Organisations";
import Survey from "./pages/Survey";
import { postUseImpressionsSurvey, priorImpressionsSurvey } from "./surveys";
import { LegalDocsApprovalPage } from "./pages/Admin";

const DOWNLOAD_URL = "https://calshare.fr/download";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const queryClient = new QueryClient();

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <NavWrapper>
        <Home />
      </NavWrapper>
    ),
  },
  {
    path: "/privacy_policy",
    element: (
      <NavWrapper>
        <PrivacyPolicy />
      </NavWrapper>
    ),
  },
  {
    path: "/go_with_calshare",
    element: <GoWithCalshare />,
  },
  {
    path: "/organisations",
    element: <Organisations />,
  },
  {
    path: "/download",
    element: <DownloadRedirect />,
  },
  {
    path: "/surveys/pre-use",
    element: (
      <NeedAuthentication>
        <Survey id="pre-use" questions={priorImpressionsSurvey} />
      </NeedAuthentication>
    ),
  },
  {
    path: "/surveys/post-use",
    element: (
      <NeedAuthentication>
        <Survey id="post-use" questions={postUseImpressionsSurvey} />
      </NeedAuthentication>
    ),
  },
  {
    path: "/wiki/:articleId?",
    element: (
      <NavWrapper>
        <CalshareWiki />
      </NavWrapper>
    ),
  },
  {
    // This route should match the in-app pattern
    path: "/trip_details/:tripId",
    element: (
      <NavWrapper>
        <TripInfos />
      </NavWrapper>
    ),
  },
  {
    // This route should match the in-app pattern
    path: "/event/:eventId",
    element: (
      <NavWrapper>
        <EventInfos />
      </NavWrapper>
    ),
  },
  {
    // This route should match the in-app pattern
    path: "/events_centre",
    element: (
      <NavWrapper>
        <EventsCentre />
      </NavWrapper>
    ),
  },
  {
    path: "/join",
    element: (
      <NavWrapper>
        <JoinPage />
      </NavWrapper>
    ),
  },
  {
    path: "/account",
    element: (
      <NavWrapper>
        <NeedAuthentication>
          <Account />
        </NeedAuthentication>
      </NavWrapper>
    ),
  },
  {
    path: "/join_as_organisation",
    element: (
      <NavWrapper>
        <RegisterOrganisation />
      </NavWrapper>
    ),
  },
  {
    path: "/leave_a_print",
    element: (
      <NavWrapper>
        <NeedAuthentication>
          <LeavePrint />
        </NeedAuthentication>
      </NavWrapper>
    ),
  },
  {
    path: "/admin/approve_legal_docs",
    element: (
      <NavWrapper>
        <NeedAuthentication role="admin">
          <LegalDocsApprovalPage />
        </NeedAuthentication>
      </NavWrapper>
    ),
  },
  {
    path: "*",
    element: <OpenInApp />,
  },
]);

const Links = () => {
  return (
    <div id="links">
      <a
        id="cgu"
        className="link"
        target="_blank"
        href="/assets/CGU_Calshare.pdf"
      >
        Conditions Générales d'Utilisation
      </a>
      <a id="privacy" className="link" target="_blank" href="/privacy_policy/">
        Politique de confidentialité
      </a>
      <a
        id="service-fees"
        className="link"
        target="_blank"
        href="/assets/Frais_Service_Calshare.pdf"
      >
        Frais de service
      </a>
      <a id="mail" className="link" href="mailto:contact@swun.fr">
        Nous contacter : contact@swun.fr
      </a>
    </div>
  );
};

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <div className="page">
        <RouterProvider router={router} />
        <div className="footer">
          <p>©️ 2025, SWUN</p>
          <Links />
        </div>
      </div>
    </QueryClientProvider>
  </React.StrictMode>
);

export { DOWNLOAD_URL };
